import {
	type NameOf,
	toPlainText as toPlainTextWith,
} from '@playbook/utils/chat-markup';

import type * as models from 'pkg/api/models';
import { fullName } from 'pkg/api/models/user/fmt';

/**
 *	Chat message markup, for the app: the parser and the renderer live in
 *	Playbook (`@playbook/utils/chat-markup`), since the chat bubble and the
 *	chat field draw the same formatting. What is the app's is who a mention
 *	names: the chat's members.
 */
export {
	MentionToken,
	DefaultLink,
	parseChatMarkup,
	renderChatMarkup,
	type LinkSegment,
	type MentionSegment,
	type NameOf,
	type RenderLink,
	type Segment,
	type TextSegment,
	type TextStyle,
} from '@playbook/utils/chat-markup';

/**
 *	Names a mentioned user from the chat's members. Members who left are
 *	still in `chat.users`, so old mentions keep their name. A user id that
 *	matches no member, or a member with no user, has no name.
 */
export function nameOfChatUser(chatUsers: models.chatUser.ChatUser[]): NameOf {
	return (id: string): string | undefined => {
		const userId = Number.parseInt(id, 10);
		const chatUser = chatUsers?.find((cu) => cu.userId === userId);
		const name = chatUser?.user
			? fullName(chatUser.user).trim()
			: '';

		return name.length > 0 ? name : undefined;
	};
}

/** How a mention reads, `@First Last`, from the chat's members, or nothing. */
export function mentionLabel(
	userId: number,
	chatUsers: models.chatUser.ChatUser[]
): string | undefined {
	const name = nameOfChatUser(chatUsers)(String(userId));

	return name ? `@${name}` : undefined;
}

interface PlainTextOptions {
	/**
	 *	Drop the formatting symbols. Only for a surface whose bubbles render
	 *	the formatting: where they show `*great*` as typed, a preview or quote
	 *	that reads `great` disagrees with the message, and `~no~` loses its
	 *	meaning. Copy always keeps them, so pasting back keeps the formatting.
	 */
	dropSymbols?: boolean;
}

/**
 *	The message as text: mentions as names, a token that matches nobody left
 *	as typed, and the formatting symbols kept unless asked to drop them.
 */
export function toPlainText(
	content: string,
	chatUsers: models.chatUser.ChatUser[],
	options: PlainTextOptions = {}
): string {
	return toPlainTextWith(content, nameOfChatUser(chatUsers), options);
}
